"use client";

import { RefObject } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import Links from "./Links";
import { WpHeaderNavResponse } from "@/lib/wordpress/getMenusFromWP";
import useTranslations from "@/i18n/useTranslations";

export default function Modal({
  open,
  headerNavData,
  onOpenChange,
}: {
  open: boolean;
  headerNavData?: WpHeaderNavResponse;
  onOpenChange: (open: boolean) => void;
  mobileCloseButtonRef?: RefObject<HTMLButtonElement | null>;
}) {
  const t = useTranslations("Navigation");
  const handleInteractOutside = (e: Event) => {
    // The mega nav button should handle its own open/close state
    const isMegaNavButton = e
      .composedPath()
      .some(
        (el: EventTarget | HTMLElement) =>
          (el as HTMLElement).id === "mega-nav-button",
      );
    if (!isMegaNavButton) {
      onOpenChange(false);
    }
  };

  const handleOpenAutoFocus = (e: Event) => {
    // Prevent default and manually focus the sign in/dashboard button, dropdown, or link
    e.preventDefault();
    setTimeout(() => {
      const modalContent = (e.target as HTMLElement).closest('[role="dialog"]');
      // First priority: Sign in or dashboard button (only if visible)
      const isVisible = (element: HTMLElement) => {
        if (!element) return false;
        const style = window.getComputedStyle(element);
        return (
          element.offsetWidth > 0 &&
          element.offsetHeight > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          style.opacity !== "0"
        );
      };

      let authButton = modalContent?.querySelector(
        'a[href*="home.edx.org"], a[href*="sign-in"], a[href*="login"], [data-testid*="sign-in"], [data-testid*="dashboard"], [class*="sign-in"], [class*="dashboard"]',
      ) as HTMLElement | null;

      // Check if the found auth button is visible
      if (authButton && !isVisible(authButton)) {
        authButton = null;
      }

      // Also check for buttons/links with sign in or dashboard text (only if visible)
      if (!authButton) {
        const allButtons = modalContent?.querySelectorAll("a, button");
        authButton =
          (Array.from(allButtons || []).find((el) => {
            const text = el.textContent?.toLowerCase() || "";
            const hasAuthText =
              text.includes("sign in") ||
              text.includes("dashboard") ||
              text.includes("login");
            return hasAuthText && isVisible(el as HTMLElement);
          }) as HTMLElement) || null;
      }

      // Second priority: Find a dropdown button (for mobile)
      const firstDropdown = modalContent?.querySelector(
        'button[aria-expanded], [role="button"]',
      ) as HTMLElement;

      // Fall back to first link if nothing else found
      const firstLink = modalContent?.querySelector("a") as HTMLElement;

      const elementToFocus = authButton || firstLink || firstDropdown;
      if (elementToFocus) {
        elementToFocus.focus();
      }
    }, 300);
  };

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="absolute top-[76px] inset-0 z-1000000 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onMouseEnter={() => onOpenChange(false)}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className="absolute top-[76px] z-1000000 left-0 flex h-[calc(100dvh-76px)] max-h-[calc(100dvh-76px)] w-screen flex-col overflow-hidden border-r bg-background p-0 shadow-lg transition ease-in-out data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left data-[state=closed]:duration-300 data-[state=open]:duration-500 lg:h-fit lg:max-h-none"
          onInteractOutside={handleInteractOutside}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{t("mainNavigationMenu")}</Dialog.Title>
          </VisuallyHidden.Root>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {headerNavData && (
              <Links
                headerNavData={headerNavData}
                onOpenChange={onOpenChange}
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
